"use client";

import { useEffect } from "react";

import { useFunPayments } from "@/contexts/queries/fun/fun-activity";
import { useWriteDexSdk } from "@/contexts/write-dex-sdk-context";
import { useAuthState } from "@/hooks/use-auth-state";
import { getServerTime } from "@/utils/server-time";

import { funImportedPayments, planFunCards } from "./fun-imported-payments";
import { useTransferProgress } from "./use-transfer-progress";
import { importedDeposits } from "./xchain-imported-deposits";

/** The only writer of `fun-deposit` cards: mirrors the server's Fun items, nothing client-side. */
export function useFunDepositTracker(): void {
  const { state, ownerWalletAddress } = useAuthState();
  const { primarySubaccountAddress } = useWriteDexSdk();
  const { transfers, addTransfer, replaceTransfer, updateTransfer, removeTransfer } =
    useTransferProgress();
  const { data } = useFunPayments();

  useEffect(() => {
    if (state !== "WALLET_CONNECTED" || !data || !ownerWalletAddress || !primarySubaccountAddress) {
      return;
    }
    const ledger = funImportedPayments(ownerWalletAddress);
    const deliveries = importedDeposits(ownerWalletAddress);
    const plan = planFunCards({
      items: data.items,
      transfers,
      imported: ledger.read(),
      importedDeliveries: deliveries.read(),
      nowMs: getServerTime(),
      subaccount: primarySubaccountAddress,
    });

    // Ledgers first: a card write re-runs this effect, which must already see the import.
    ledger.add(...plan.imported);
    deliveries.add(...plan.importedDeliveries);
    for (const op of plan.ops) {
      switch (op.kind) {
        case "add":
          addTransfer(op.card);
          break;
        case "replace":
          replaceTransfer(op.oldId, op.card);
          break;
        case "update":
          updateTransfer(op.id, op.update);
          break;
        case "remove":
          removeTransfer(op.id);
          break;
      }
    }
  }, [
    state,
    data,
    transfers,
    ownerWalletAddress,
    primarySubaccountAddress,
    addTransfer,
    replaceTransfer,
    updateTransfer,
    removeTransfer,
  ]);
}
